import React from 'react';
import { Modal } from 'antd';
import AiSummarySectionGroup from './AiSummarySectionGroup';

/**
 * FullSummaryModal Component
 * Implements the expanded AI Inbox Summary panel/modal (Spec §9, §10).
 * Displays full context, grouped sections, and operational deep-link actions.
 */
export default function FullSummaryModal({
    visible,
    onClose,
    summaryData,
    selectedPeriod,
    selectedProject = 'All',
    onItemActionClick
}) {
    // 5 standard logical sections per specification §10
    const sections = [
        'Requires Your Attention',
        'Recent Progress',
        'Important Changes',
        'Conversations & Mentions',
        'Other Relevant Updates'
    ];

    const groupedItems = sections.reduce((acc, sectionName) => {
        const matching = summaryData?.items?.filter((item) => item.section === sectionName) || [];
        if (matching.length > 0) {
            acc[sectionName] = matching;
        }
        return acc;
    }, {});

    const handleItemClick = (item) => {
        onClose?.();
        onItemActionClick?.(item);
    };

    return (
        <Modal
            open={visible}
            visible={visible}
            onCancel={onClose}
            footer={null}
            width={760}
            centered
            className="inbox-full-summary-modal"
            title={
                <div className="flex items-center gap-2.5 pb-1">
                    <div className="w-7 h-7 rounded-lg bg-blue-100/70 dark:bg-blue-950/60 text-[#03488A] dark:text-[#00AAF1] flex items-center justify-center">
                        <span className="material-symbols-outlined text-lg">auto_awesome</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-headline font-bold text-base text-[#003263] dark:text-[#a7c8ff]">
                            Inbox Summary — {selectedPeriod || 'Past Week'}
                        </span>
                        {selectedProject && selectedProject !== 'All' ? (
                            <span
                                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100/80 dark:bg-blue-900/50 text-[#03488A] dark:text-[#00AAF1] border border-blue-200/80 dark:border-blue-800/60 max-w-[220px] truncate shadow-2xs"
                                title={`Filtered by project: ${selectedProject}`}
                            >
                                <span className="material-symbols-outlined text-xs">folder_open</span>
                                <span className="truncate">{selectedProject}</span>
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200/60 dark:border-slate-700/60">
                                <span className="material-symbols-outlined text-xs">domain</span>
                                All Projects
                            </span>
                        )}
                        {summaryData?.updatedAt && (
                            <span className="text-[11px] text-slate-400 font-normal ml-1">
                                (Updated {(() => {
                                    const d = new Date(summaryData.updatedAt);
                                    return !isNaN(d.getTime()) ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : String(summaryData.updatedAt);
                                })()})
                            </span>
                        )}
                    </div>
                </div>
            }
        >
            <div className="space-y-6 pt-2 max-h-[75vh] overflow-y-auto pr-1">
                {/* Context Banner / Macro Overview */}
                <div className="bg-blue-50/70 dark:bg-blue-900/20 border border-blue-200/70 dark:border-blue-800/40 rounded-xl p-3.5 text-xs text-[#03488A] dark:text-[#00AAF1] leading-relaxed flex items-center gap-2.5">
                    <span className="material-symbols-outlined text-base flex-shrink-0">info</span>
                    <span>
                        {summaryData?.overviewText || 'Here is what has changed across your authorized project activity over the selected period. Clicking any item or action button navigates directly into the underlying ERP record.'}
                    </span>
                </div>

                {/* Categorized Sections or Empty State */}
                {Object.keys(groupedItems).length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                        <span className="material-symbols-outlined text-4xl text-slate-300">description</span>
                        <p className="text-sm mt-2">No summary briefing items found for this period.</p>
                    </div>
                ) : (
                    Object.entries(groupedItems).map(([sectionTitle, items]) => (
                        <AiSummarySectionGroup
                            key={sectionTitle}
                            sectionTitle={sectionTitle}
                            items={items}
                            onItemActionClick={handleItemClick}
                        />
                    ))
                )}
            </div>
        </Modal>
    );
}
